import { For, Show, createSignal, type JSX } from "solid-js";
import { Band } from "../../components/Band.tsx";
import { CoverageBar } from "../../components/CoverageBar.tsx";
import { SelectField } from "../../components/Form.tsx";
import { SourceTag } from "../../components/SourceTag.tsx";
import { Table } from "../../components/Table.tsx";
import { type Tab, Tabs } from "../../components/Tabs.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { formatMinimum, formatMoment, formatScore, formatWeight } from "../../lib/format.ts";
import type { ReportSnapshot } from "../../api/domain.ts";
import {
  capabilityLabel,
  capabilityRows,
  personaColumns,
  rankProducts,
  topicRows,
  type PersonaColumn,
  type PersonaEntry,
} from "./report-view.ts";
import styles from "./admin.module.scss";

export type ReportViewProps = {
  snapshot: ReportSnapshot;
};

type PersonaEntryProps = {
  snapshot: ReportSnapshot;
  entry: PersonaEntry;
};

const PersonaVerdict = (props: PersonaEntryProps) => {
  const i18n = useI18n();

  return (
    <div class={styles.stack}>
      <div class={styles.inline}>
        <strong>{props.entry.name}</strong>
        {/*
          The weighted score is the ranking number, so it carries the band; the
          global one follows as context and is labelled as such.
        */}
        <Band score={props.entry.weightedScore} showScore />
        <span class={styles.note}>
          {i18n.t().reports.globalScore}:{" "}
          {props.entry.score === null
            ? i18n.t().common.notAssessed
            : formatScore(props.entry.score, i18n.locale())}
        </span>
        <Show when={props.entry.excluded}>
          <span class={styles.excluded}>{i18n.t().reports.excluded}</span>
        </Show>
      </div>

      <For each={props.entry.violations}>
        {(violation) => (
          <div class={styles.violation}>
            <strong>{capabilityLabel(props.snapshot, violation.capability)}</strong>
            <span>
              {i18n.t().reports.minimum}:{" "}
              {formatMinimum(i18n.t(), violation.minimum, i18n.locale())} ·{" "}
              {i18n.t().reports.actual}:{" "}
              {violation.actual === null
                ? i18n.t().common.notAssessed
                : formatScore(violation.actual, i18n.locale())}
            </span>
            <span class={styles.rationale}>{violation.rationale}</span>
            <span class={styles.note}>{i18n.t().reports.contributing}</span>
            <For each={violation.contributing}>
              {(answer) => (
                <span class={styles.inline}>
                  <SourceTag source={answer.source} confidence={answer.confidence} />
                  {answer.question} · {formatScore(answer.value, i18n.locale())} · {answer.author}
                </span>
              )}
            </For>
          </div>
        )}
      </For>

      <For each={props.entry.open}>
        {(open) => (
          <div class={styles.open}>
            <strong>{capabilityLabel(props.snapshot, open.capability)}</strong>
            <span>
              {i18n.t().reports.minimum}: {formatMinimum(i18n.t(), open.minimum, i18n.locale())} ·{" "}
              {i18n.t().common.notAssessed}
            </span>
            <span class={styles.rationale}>{open.rationale}</span>
          </div>
        )}
      </For>
    </div>
  );
};

type PersonaPanelProps = {
  snapshot: ReportSnapshot;
  column: PersonaColumn;
};

/**
 * One persona's ranking, plus the weights that produced it.
 *
 * A rating of "irrelevant" can lift a product several places, so the applied
 * weights are shown rather than merely applied: the skew has to be auditable
 * from the report itself. The weights stay open for the same reason.
 */
const PersonaPanel = (props: PersonaPanelProps) => {
  const i18n = useI18n();

  return (
    <article class={styles.personaCard}>
      <header class={styles.stack}>
        <h3 class={styles.personaName}>{props.column.name}</h3>
        <Show when={props.column.reordered}>
          <p class={styles.note} title={i18n.t().reports.reorderedHint}>
            {i18n.t().reports.reorderedByRatings}
          </p>
        </Show>
      </header>

      <For each={props.column.entries}>
        {(entry) => <PersonaVerdict snapshot={props.snapshot} entry={entry} />}
      </For>

      <Show when={props.column.weights.length > 0}>
        <div class={styles.stack}>
          <h4 class={styles.subheading}>{i18n.t().reports.appliedWeights}</h4>
          <Table
            rows={props.column.weights}
            rowKey={(weight) => weight.capability}
            emptyLabel={i18n.t().relevancy.noRatings}
            columns={[
              {
                key: "capability",
                header: i18n.t().common.capability,
                cell: (weight) => weight.label,
              },
              {
                key: "rating",
                header: i18n.t().relevancy.rating,
                cell: (weight) => i18n.t().relevancy.scale[weight.rating],
              },
              {
                key: "weight",
                header: i18n.t().relevancy.weight,
                align: "end",
                cell: (weight) => formatWeight(weight.weight, i18n.locale()),
              },
            ]}
          />
        </div>
      </Show>
    </article>
  );
};

/** The ranking. The report's own metadata sits in the reading's head, not here. */
export const RankingSection = (props: ReportViewProps) => {
  const i18n = useI18n();
  const ranked = () => rankProducts(props.snapshot);

  return (
    <div class={styles.stack}>
      {/*
        A report is evaluated in its owner's world. When that world holds no
        answered product inside the view, the table below is empty for the
        customer too, and the consultant should learn that here, not from them.
      */}
      <Show when={props.snapshot.input.products.length === 0}>
        <p class={styles.problem}>{i18n.t().reports.noProducts}</p>
      </Show>
      <Show
        when={
          props.snapshot.input.products.length > 0 && props.snapshot.input.answers.length === 0
        }
      >
        <p class={styles.problem}>{i18n.t().reports.noAnswers}</p>
      </Show>
      <Show when={props.snapshot.evaluation.requirements_skipped}>
        <p class={styles.note}>{i18n.t().reports.requirementsSkipped}</p>
      </Show>

      <Table
        rows={ranked()}
        rowKey={(product) => product.product}
        emptyLabel={i18n.t().common.empty}
        columns={[
          { key: "name", header: i18n.t().common.product, cell: (product) => product.name },
          {
            key: "score",
            header: i18n.t().common.score,
            align: "end",
            cell: (product) =>
              product.score === null
                ? i18n.t().common.notAssessed
                : formatScore(product.score, i18n.locale()),
          },
          {
            key: "band",
            header: i18n.t().common.band,
            cell: (product) => <Band score={product.score} />,
          },
          {
            key: "coverage",
            header: i18n.t().common.coverage,
            cell: (product) => <CoverageBar {...product.coverage} />,
          },
        ]}
      />
    </div>
  );
};

/** Every topic as a row, every ranked product as a column of bands. */
export const TopicSection = (props: ReportViewProps) => {
  const i18n = useI18n();
  const ranked = () => rankProducts(props.snapshot);
  const rows = () => topicRows(props.snapshot);

  return (
    <Table
      rows={rows()}
      rowKey={(row) => row.topic}
      emptyLabel={i18n.t().common.empty}
      columns={[
        { key: "topic", header: i18n.t().common.topic, cell: (row) => row.title },
        {
          key: "weight",
          header: i18n.t().common.weight,
          align: "end",
          cell: (row) => formatWeight(row.weight, i18n.locale()),
        },
        ...ranked().map((product, index) => ({
          key: `product-${product.product}`,
          header: product.name,
          cell: (row: { cells: { score: number | null; band: string | null; coverage: { answered: number; total: number } }[] }) => {
            const cell = row.cells[index];
            if (cell === undefined) return "";
            return (
              <span class={styles.stack}>
                <Band score={cell.score} band={cell.band as never} showScore />
                <CoverageBar {...cell.coverage} hideLabel />
              </span>
            );
          },
        })),
      ]}
    />
  );
};

/** The personas beside one another, never merged into a single verdict. */
export const PersonaSection = (props: ReportViewProps) => {
  const columns = () => personaColumns(props.snapshot);

  return (
    <div class={styles.columns}>
      <For each={columns()}>
        {(column) => <PersonaPanel snapshot={props.snapshot} column={column} />}
      </For>
    </div>
  );
};

/** One product at a time, down to the answers each capability rests on. */
export const CapabilitySection = (props: ReportViewProps) => {
  const i18n = useI18n();
  const [drilled, setDrilled] = createSignal("");
  const ranked = () => rankProducts(props.snapshot);

  return (
    <div class={styles.stack}>
      <SelectField
        label={i18n.t().reports.drillDown}
        placeholder={i18n.t().common.none}
        choices={ranked().map((product) => ({ id: product.product, label: product.name }))}
        value={drilled()}
        onPick={setDrilled}
      />
      <Show when={drilled() !== ""}>
        <Table
          rows={capabilityRows(props.snapshot, drilled())}
          rowKey={(row) => row.capability}
          emptyLabel={i18n.t().common.empty}
          columns={[
            { key: "topic", header: i18n.t().common.topic, cell: (row) => row.topic },
            { key: "capability", header: i18n.t().common.capability, cell: (row) => row.label },
            {
              key: "band",
              header: i18n.t().common.band,
              cell: (row) => <Band score={row.score} band={row.band} showScore />,
            },
            {
              key: "coverage",
              header: i18n.t().common.coverage,
              cell: (row) => <CoverageBar {...row.coverage} />,
            },
            {
              key: "answers",
              header: i18n.t().reports.contributing,
              cell: (row) => (
                <span class={styles.stack}>
                  <For each={row.answers}>
                    {(answer) => (
                      <span class={styles.inline}>
                        <SourceTag source={answer.source} confidence={answer.confidence} />
                        {answer.question} · {formatScore(answer.value, i18n.locale())}
                      </span>
                    )}
                  </For>
                </span>
              ),
            },
          ]}
        />
      </Show>
    </div>
  );
};

/** The four readings of one evaluation, in the order a reader works through them. */
export const reportSections = ["ranking", "topics", "personas", "capabilities"] as const;

export type ReportSection = (typeof reportSections)[number];

/** The section an id names, falling back to the ranking for anything unknown. */
export const reportSectionOf = (raw: string | undefined): ReportSection =>
  reportSections.find((section) => section === raw) ?? "ranking";

export type ReportReadingProps = {
  snapshot: ReportSnapshot;
  /**
   * The open section. Left out, the reading keeps its own; the saved report page
   * hands it in from the URL so a pasted link opens on the same section.
   */
  section?: ReportSection;
  onPick?: (section: ReportSection) => void;
  /** What the reader may do with this report here: save, lock, download. */
  actions?: JSX.Element;
  /** Names the tab strip for assistive technology: the report's id, or "preview". */
  label: string;
};

/**
 * One evaluation, read as a report: its facts in a head line, then the ranking,
 * the bands per topic, the personas side by side and the answers behind every
 * verdict, one section at a time.
 *
 * The builder's preview and the saved report page both render this, so what the
 * consultant checks before saving is what the customer opens afterwards.
 */
export const ReportReading = (props: ReportReadingProps) => {
  const i18n = useI18n();
  const [own, setOwn] = createSignal<ReportSection>("ranking");
  const section = () => props.section ?? own();
  const pick = (id: string) => {
    const next = reportSectionOf(id);
    setOwn(next);
    props.onPick?.(next);
  };

  const tabs = (): Tab[] => [
    {
      id: "ranking",
      label: i18n.t().reports.ranking,
      content: () => <RankingSection snapshot={props.snapshot} />,
    },
    {
      id: "topics",
      label: i18n.t().reports.perTopic,
      content: () => <TopicSection snapshot={props.snapshot} />,
    },
    {
      id: "personas",
      label: i18n.t().reports.personaRankings,
      content: () => <PersonaSection snapshot={props.snapshot} />,
    },
    {
      id: "capabilities",
      label: i18n.t().reports.capabilities,
      content: () => <CapabilitySection snapshot={props.snapshot} />,
    },
  ];

  return (
    <section class={styles.reading} aria-label={props.label}>
      <div class={styles.readingHead}>
        <dl class={styles.facts}>
          <div class={styles.fact}>
            <dt>{i18n.t().reports.asOf}</dt>
            <dd>{formatMoment(props.snapshot.as_of, i18n.locale())}</dd>
          </div>
          <div class={styles.fact}>
            <dt>{i18n.t().reports.formulaVersion}</dt>
            <dd>{props.snapshot.formula_version}</dd>
          </div>
          <div class={styles.fact}>
            <dt>{i18n.t().reports.status}</dt>
            <dd>
              <Show
                when={props.snapshot.locked_at !== null}
                fallback={<span class={styles.statusLive}>{i18n.t().reports.live}</span>}
              >
                <span class={styles.statusLocked}>
                  {i18n.t().reports.locked}: {formatMoment(props.snapshot.locked_at ?? 0, i18n.locale())}
                </span>
              </Show>
            </dd>
          </div>
        </dl>
        <Show when={props.actions !== undefined}>
          <div class={styles.inline}>{props.actions}</div>
        </Show>
      </div>

      <Tabs tabs={tabs()} active={section()} onPick={pick} label={props.label} />
    </section>
  );
};
